import { useEffect, useState, useRef } from "react";
import { doc, onSnapshot, updateDoc } from "firebase/firestore";
import { db } from "../firebase";

// time must differ (ms) to trigger correction
const DRIFT_THRESHOLD = 1000;

export function useSharedTimer(roomId, localTimerState, isAdmin) {
  const [sharedState, setSharedState] = useState(null);
  const lastPush = useRef(0); // prevent write loops
  const {
    running: localRunning,
    baseSeconds: localBaseSeconds,
    timerSeconds: localTimerSeconds,
    ...remainingLocalTimerState
  } = localTimerState;

  // Subscribe to remote changes
  useEffect(() => {
    if (!roomId) return undefined;

    const roomRef = doc(db, "timers", roomId);
    const unsub = onSnapshot(roomRef, (docSnap) => {
      const data = docSnap.data();
      if (!data) return;

      // Don't react to our own recent update
      if (Date.now() - lastPush.current < 300) return;

      setSharedState(data);
    });

    return () => unsub();
  }, [roomId]);

  // ⏫ Push local state to Firestore if admin
  useEffect(() => {
    if (!isAdmin || !roomId) return undefined;

    const roomRef = doc(db, "timers", roomId);

    const pushUpdate = async () => {
      lastPush.current = Date.now();
      await updateDoc(roomRef, {
        ...remainingLocalTimerState,
        running: localRunning,
        baseSeconds: localBaseSeconds,
        timerSeconds: localTimerSeconds,
        lastUpdated: Date.now(),
      });
    };

    pushUpdate();
  }, [
    isAdmin,
    localBaseSeconds,
    localRunning,
    localTimerSeconds,
    remainingLocalTimerState,
    roomId,
  ]);

  // ⏳ Apply remote state to local timer (for viewers)
  const getCorrectedSeconds = () => {
    if (!sharedState || isAdmin) return null;

    const { lastUpdated, baseSeconds } = sharedState;
    const now = Date.now();
    const elapsed = Math.floor((now - lastUpdated) / 1000);
    const corrected = Math.max(0, baseSeconds - elapsed);

    // Only return if drift is meaningful
    if (Math.abs(corrected - localTimerSeconds) >= DRIFT_THRESHOLD / 1000) {
      return corrected;
    }
    return null;
  };

  return {
    sharedState,
    correctedTimerSeconds: getCorrectedSeconds(),
  };
}
